import { Router } from './Router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import './styles/app.scss';

const TWO_MINS_MS = 120000;
const FIVE_MINS_MS = 300000;
const DEFAULT_API_BASE_URL = 'https://hbh70bc4ui.execute-api.ap-southeast-2.amazonaws.com/prod';
const API_BASE_URL = (process.env.VITE_API_BASE_URL || DEFAULT_API_BASE_URL).replace(/\/+$/, '');

const createQueryClient = (apiKey: string) => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        queryFn: async ({ queryKey }) => {
          const path = (queryKey as string[])[0].replace(/^\/+/, '');
          const response = await fetch(`${API_BASE_URL}/${path}`, {
            headers: {
              'x-api-key': apiKey,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error('Network response was not ok');
          }

          return await response.json();
        },
        refetchInterval: (_data, query) => (query.state.status === 'error' ? TWO_MINS_MS : FIVE_MINS_MS),
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000)
      }
    }
  });
};

function getSessionApiKey() {
  try {
    return localStorage.getItem('apiKey');
  } catch {
    return null;
  }
}
export default function App() {
  const [inputApiKey, setInputApiKey] = useState('');
  const [apiKey, setApiKey] = useState(getSessionApiKey());
  // A fresh client would drop the cached dashboard, which turns the next failed
  // refetch into the full-screen error instead of the stale badge.
  const queryClient = useMemo(() => (apiKey ? createQueryClient(apiKey) : null), [apiKey]);

  useEffect(() => {
    if (!apiKey) {
      return;
    }

    localStorage.setItem('apiKey', apiKey);
  }, [apiKey]);

  const signOut = useCallback(() => {
    localStorage.removeItem('apiKey');
    setApiKey(null);
    setInputApiKey('');
  }, []);

  if (!apiKey || !queryClient) {
    return (
      <div className="app__login">
        <main className="app__login__card">
          <div className="app__login__logo" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2a10 10 0 0 0-3.16 19.49c.5.09.68-.22.68-.48v-1.7c-2.78.6-3.37-1.34-3.37-1.34-.45-1.16-1.11-1.47-1.11-1.47-.9-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.89 1.52 2.34 1.08 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.65 0 0 .84-.27 2.75 1.02a9.6 9.6 0 0 1 5 0c1.91-1.29 2.75-1.02 2.75-1.02.55 1.38.2 2.4.1 2.65.64.7 1.03 1.59 1.03 2.68 0 3.84-2.34 4.68-4.57 4.93.36.31.68.92.68 1.85v2.74c0 .27.18.58.69.48A10 10 0 0 0 12 2Z"
                fill="#fff"
              />
            </svg>
          </div>
          <h1 className="app__login__title font-display">Engineering Dashboard</h1>
          <p className="app__login__helper">Enter your API key to continue</p>
          <form
            className="app__login__form"
            onSubmit={(event) => {
              event.preventDefault();
              if (inputApiKey) {
                setApiKey(inputApiKey);
              }
            }}
          >
            <input
              className="app__login__input"
              type="password"
              aria-label="API key"
              onChange={({ target: { value } }) => setInputApiKey(value)}
              placeholder="API Key"
            />
            <button className="app__login__submit" type="submit" disabled={!inputApiKey}>
              Sign In
            </button>
          </form>
          <p className="app__login__hint">
            Find the key in Keeper under <strong>NeonNow Product Engineer Dashboard: API Key</strong>
          </p>
        </main>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router onSignOut={signOut} />
    </QueryClientProvider>
  );
}
